"use client";

import { type FormEvent, useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion, type Variants } from "framer-motion";

import { Button } from "@/components/ui/button";
import { BrailleLoadingIndicator } from "@/components/ui/braille-loading-indicator";
import { cn } from "@/lib/utils";

type Status = "idle" | "submitting" | "submitted";

const EASE = [0.22, 1, 0.36, 1] as const;

const LEGAL_DISCLAIMER =
  "Zom AI is an AI-powered software platform offered by Zom Technologies, LLC for use by registered investment advisers to access information, analyze data, and support operational and advisory workflows. Zom Technologies is a financial technology company and is not registered as a broker-dealer or investment adviser; any investment-related analysis, recommendations, or other outputs produced through Zom AI are provided for the RIA’s review and do not replace the RIA’s professional judgment, supervision, or compliance responsibilities. AI-generated outputs may be incomplete, inaccurate, or outdated and should be reviewed and verified by the RIA before being relied upon or communicated to clients.";

// Both fields use the standalone page's geometry and a visible keyboard ring.
const PILL_CLASS =
  "flex has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-zl-warm-white has-[input:focus-visible]:ring-offset-2 has-[input:focus-visible]:ring-offset-zl-obsidian h-[var(--zl-field-height)] w-full items-center rounded-[var(--zl-field-radius)] border-[length:var(--zl-field-border-width)] border-solid border-zl-border bg-zl-field";

const INPUT_CLASS =
  "h-full min-w-0 flex-1 border-0 bg-transparent px-[var(--zl-field-padding)] py-0 font-zl-sans text-[1.02rem] font-normal text-zl-fg placeholder:text-[color:var(--zl-placeholder)] focus-visible:outline-none";

export default function ComingSoonPage() {
  const reduceMotion = useReducedMotion();

  const [email, setEmail] = useState("");
  const [firmName, setFirmName] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubmitting = status === "submitting";
  const isSubmitted = status === "submitted";

  const container: Variants = {
    hidden: {},
    show: {
      transition: reduceMotion
        ? {}
        : { staggerChildren: 0.12, delayChildren: 0.04 },
    },
  };

  const item: Variants = reduceMotion
    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 22 },
        show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: EASE } },
      };

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting || isSubmitted) return;

    setStatus("submitting");
    setError(null);

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          firm_name: firmName.trim(),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: unknown;
        already_registered?: boolean;
      };

      if (!res.ok) {
        // The error must be a string. The backend can return a structured
        // error object ({type, message, error_id, path}); rendering that object
        // as a React child throws (React #31) and white-screens the page.
        const message =
          typeof data.error === "string"
            ? data.error
            : "Something went wrong. Please try again.";
        setError(message);
        setStatus("idle");
        return;
      }

      setAlreadyRegistered(Boolean(data.already_registered));
      setStatus("submitted");
    } catch {
      setError("Network error. Please check your connection and try again.");
      setStatus("idle");
    }
  }

  return (
    <motion.main
      variants={container}
      initial={false}
      animate="show"
      className="zl-coming-soon font-zl-sans text-zl-fg relative isolate flex min-h-svh w-full flex-col items-center px-[var(--zl-page-gutter)] py-[var(--zl-page-padding)]"
    >
      {/* Brand mark */}
      <motion.div variants={item} className="flex-none">
        <Image
          src="/images/zom-marketing/logo-white-pictorial.png"
          alt="Zom"
          width={34}
          height={34}
          priority
          className="zl-phone:h-[30px] block h-[34px] w-auto"
        />
      </motion.div>

      <div className="zl-phone:gap-[var(--zl-content-gap-phone)] flex w-full max-w-[var(--zl-page-width)] flex-auto flex-col items-center justify-center gap-[var(--zl-content-gap)] text-center">
        <motion.div variants={item} className="mt-[var(--zl-eyebrow-margin)]">
          <span className="text-zl-text-eyebrow zl-phone:text-[11px] zl-phone:tracking-[0.18em] text-[12px] font-normal tracking-[0.14em] uppercase">
            Launching Soon
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-zl-display text-zl-fg zl-tablet:max-w-[16ch] zl-phone:max-w-[17ch] zl-phone:text-[clamp(2rem,9vw,2.95rem)] zl-phone:leading-[1.07] zl-phone:tracking-[-0.015em] m-0 max-w-[22ch] text-[clamp(2.4rem,7vw,5.3rem)] leading-[1.04] font-normal tracking-[-0.012em] text-balance"
        >
          Making <span className="whitespace-nowrap">high-quality</span>
          {" financial intelligence universal."}
        </motion.h1>

        {/* Subhead */}
        <motion.p
          variants={item}
          className="text-zl-text-muted zl-tablet:max-w-[36em] zl-phone:max-w-[34em] zl-phone:leading-[1.5] m-0 max-w-[48em] text-[clamp(1rem,1.15vw,1.16rem)] leading-[1.55] font-light tracking-[0.004em] text-pretty"
        >
          Zom brings best practice to every part of a financial advisory
          firm&apos;s operation, so advisors can serve clients better, operate
          in less time, and build more valuable, more profitable firms.
        </motion.p>

        {/* Form / success */}
        <motion.div
          variants={item}
          className="flex w-full max-w-[var(--zl-form-width)] flex-col items-center gap-3"
        >
          {isSubmitted ? (
            <div
              role="status"
              aria-live="polite"
              className="border-zl-border bg-zl-field flex w-full flex-col items-center gap-[13px] rounded-2xl border border-solid px-[26px] py-7"
            >
              <span
                aria-hidden
                className="border-zl-border flex h-10 w-10 items-center justify-center rounded-full border border-solid bg-[color-mix(in_srgb,var(--zl-warm-white)_10%,transparent)]"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--zl-warm-white)"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              <p className="font-zl-display text-zl-fg m-0 text-[1.4rem] leading-[1.2] font-normal">
                {alreadyRegistered
                  ? "You're already on the list"
                  : "You're on the list"}
              </p>
              <p className="text-zl-text-soft m-0 max-w-[42ch] text-[0.95rem] leading-[1.5] font-light text-balance">
                {alreadyRegistered
                  ? `${email} is already on the waitlist. We will be in touch when access opens.`
                  : `Thank you. We will be in touch at ${email} when access opens.`}
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              aria-label="Join the waitlist"
              className="flex w-full flex-col gap-2.5"
            >
              {/* Firm name */}
              <div className={PILL_CLASS}>
                <input
                  type="text"
                  autoComplete="organization"
                  required
                  aria-label="Firm name"
                  placeholder="Firm name"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  disabled={isSubmitting}
                  className={INPUT_CLASS}
                />
              </div>

              {/* Email */}
              <div className={PILL_CLASS}>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  aria-label="Work email"
                  placeholder="you@firm.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className={INPUT_CLASS}
                />
              </div>

              <div className="flex w-full justify-center">
                <Button
                  type="submit"
                  variant="outline"
                  aria-busy={isSubmitting}
                  disabled={isSubmitting}
                  className={cn(
                    "bg-zl-warm-white font-zl-sans text-zl-obsidian hover:bg-zl-warm-white hover:text-zl-obsidian focus-visible:ring-zl-warm-white focus-visible:ring-offset-zl-obsidian relative h-[var(--zl-button-height)] rounded-[var(--zl-button-radius)] border-0 px-[var(--zl-button-padding)] text-[0.95rem] font-normal focus-visible:ring-2 focus-visible:ring-offset-2",
                    isSubmitting && "cursor-wait",
                  )}
                >
                  {isSubmitting && (
                    <BrailleLoadingIndicator
                      prefersReducedMotion={Boolean(reduceMotion)}
                    />
                  )}
                  <span className="relative z-[1]">
                    {isSubmitting ? "Joining" : "Join waitlist"}
                  </span>
                </Button>
              </div>
            </form>
          )}

          {error && (
            <p
              role="status"
              aria-live="polite"
              className="text-zl-danger m-0 w-full text-[0.82rem]"
            >
              {error}
            </p>
          )}
        </motion.div>
      </div>

      <motion.footer
        variants={item}
        className="text-zl-text-faint mt-[var(--zl-footer-margin)] w-[min(100%,980px)] flex-none pt-[var(--zl-footer-padding)] text-center text-[clamp(0.75rem,0.74vw,0.82rem)] leading-[1.65] font-light text-pretty [border-top:1px_solid_color-mix(in_srgb,var(--zl-border)_38%,transparent)]"
      >
        <p className="m-0">{LEGAL_DISCLAIMER}</p>
      </motion.footer>
    </motion.main>
  );
}
